import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import styles from "@components/Tooltip.module.css";
import useOpener from "@hooks/useOpener";
import { generateHTMLId } from "@utils/htmlUtils";

type Direction = "top" | "bottom" | "left" | "right";

export type TooltipTriggerProps = {
  "aria-describedby": string;
  onBlur: () => void;
  onFocus: () => void;
  role: string;
};

type Props = {
  children: (props: TooltipTriggerProps) => React.ReactNode;
  content?: React.ReactNode;
  delay?: number;
  expand?: boolean;
  initialDirection?: Direction;
};

const Tooltip = ({ children, content, delay = 300, expand = false, initialDirection = "top" }: Props) => {
  let timeout: NodeJS.Timeout | null = null;
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const activeOpener = useOpener();
  const [direction, setDirection] = useState<Direction>(initialDirection);

  const wrapperClasses = [styles.tooltipWrapper];
  if (expand) wrapperClasses.push("block");

  const showTip = () => {
    if (activeOpener.isOpen) return;

    timeout = setTimeout(() => {
      activeOpener.open();
    }, delay);
  };

  const hideTip = () => {
    if (tooltipRef.current && tooltipRef.current.contains(document.activeElement)) {
      return;
    }
    if (timeout) clearTimeout(timeout);
    activeOpener.close();
  };

  const primaryOverflowDirection = (triggerRect: DOMRect, tooltipRect: DOMRect, initialDirection: Direction) => {
    let newDirection = initialDirection;

    if (initialDirection === "left" && triggerRect.left - tooltipRect.width < 0) {
      newDirection = "right";
    } else if (initialDirection === "right" && triggerRect.right + tooltipRect.width > window.innerWidth) {
      newDirection = "left";
    } else if (initialDirection === "top" && triggerRect.top - tooltipRect.height < 0) {
      newDirection = "bottom";
    } else if (initialDirection === "bottom" && triggerRect.bottom + tooltipRect.height > window.innerHeight) {
      newDirection = "top";
    }

    return newDirection;
  };

  const horizontalOverflowDirection = (triggerRect: DOMRect, tooltipRect: DOMRect, initialDirection: Direction) => {
    let newDirection = initialDirection;

    if (newDirection === "left" || newDirection === "right") {
      if (triggerRect.top - tooltipRect.height < 0) {
        newDirection = "bottom";
      } else if (triggerRect.bottom + tooltipRect.height > window.innerHeight) {
        newDirection = "top";
      }
    }

    return newDirection;
  };

  const verticalOverflowDirection = (triggerRect: DOMRect, tooltipRect: DOMRect, initialDirection: Direction) => {
    let newDirection = initialDirection;

    if (newDirection === "top" || newDirection === "bottom") {
      const triggerCenterX = triggerRect.left + triggerRect.width / 2;
      if (triggerCenterX - tooltipRect.width / 2 < 0) {
        newDirection = "right";
      } else if (triggerCenterX + tooltipRect.width / 2 > window.innerWidth) {
        newDirection = "left";
      }
    }

    return newDirection;
  };

  const calculateDirection = (triggerRect: DOMRect, tooltipRect: DOMRect, initialDirection: Direction) => {
    let newDirection = primaryOverflowDirection(triggerRect, tooltipRect, initialDirection);
    newDirection = horizontalOverflowDirection(triggerRect, tooltipRect, newDirection);
    newDirection = verticalOverflowDirection(triggerRect, tooltipRect, newDirection);

    return newDirection;
  };

  const getHorizontalCenter = (triggerRect: DOMRect, tooltipRect: DOMRect): number =>
    triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;

  const getVerticalCenter = (triggerRect: DOMRect, tooltipRect: DOMRect): number =>
    triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;

  const applyStyles = (tooltipElement: HTMLDivElement, top: number, left: number): void => {
    tooltipElement.style.top = `${top + window.scrollY}px`;
    tooltipElement.style.left = `${left + window.scrollX}px`;
  };

  const applyPosition = (triggerRect: DOMRect, tooltipRect: DOMRect, newDirection: Direction): void => {
    if (!tooltipRef.current) return;

    const horizontalCenter = getHorizontalCenter(triggerRect, tooltipRect);
    const verticalCenter = getVerticalCenter(triggerRect, tooltipRect);

    let top: number;
    let left: number;

    switch (newDirection) {
      case "top":
        top = triggerRect.top - tooltipRect.height;
        left = horizontalCenter;
        break;
      case "bottom":
        top = triggerRect.bottom;
        left = horizontalCenter;
        break;
      case "left":
        top = verticalCenter;
        left = triggerRect.left - tooltipRect.width - 10;
        break;
      case "right":
        top = verticalCenter;
        left = triggerRect.right + 10;
        break;
      default:
        return;
    }

    applyStyles(tooltipRef.current, top, left);
  };

  useEffect(() => {
    const updatePosition = () => {
      if (activeOpener.isOpen && tooltipRef.current && triggerRef.current) {
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();

        const newDirection = calculateDirection(triggerRect, tooltipRect, direction);

        applyPosition(triggerRect, tooltipRect, newDirection);

        if (newDirection !== direction) {
          setDirection(newDirection);
        }
      }
    };

    updatePosition();

    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOpener.isOpen, initialDirection]);

  const [id] = useState<string>(generateHTMLId());

  return (
    <div
      aria-live="polite"
      className={wrapperClasses.join(" ")}
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
      role="region"
    >
      <div ref={triggerRef}>
        {children({
          "aria-describedby": id,
          onBlur: hideTip,
          onFocus: showTip,
          role: "tooltip"
        })}
      </div>

      {activeOpener.isOpen && (
        <>
          {createPortal(
            <div className={`${styles.tip} ${styles[direction]}`} id={id} onMouseEnter={showTip} ref={tooltipRef}>
              {content}
            </div>,
            document.body
          )}
        </>
      )}
    </div>
  );
};

export default Tooltip;
